import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Target, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProgramDetailPage() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/programs/${slug}`)
      .then((r) => setProgram(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 gap-4">
        <p className="font-body text-slate-400">Programme non trouve</p>
        <Link to="/programmes" className="text-blue-400 font-body text-sm">Retour aux programmes</Link>
      </div>
    );
  }

  const kf = program.key_figures || {};

  return (
    <div data-testid="program-detail-page" className="pt-24">
      {/* Hero */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${program.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 to-slate-950" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/programmes" className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 font-body text-sm mb-8 transition-colors" data-testid="back-to-programs">
            <ArrowLeft className="w-4 h-4" /> Retour aux programmes
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-6 leading-[1.1]">
            {program.title}
          </h1>
          <p className="font-body text-base md:text-lg font-light text-slate-300 max-w-3xl">
            {program.long_description || program.description}
          </p>
        </div>
      </section>

      <div className="section-divider" />

      {/* Key Figures */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {Object.entries(kf).map(([key, val]) => (
              <div key={key} className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 text-center">
                <div className="font-body text-3xl md:text-4xl font-bold text-blue-400 mb-2">{val}</div>
                <div className="font-body text-xs uppercase tracking-widest text-slate-400">
                  {key.replace(/_/g, ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Details */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-50">Objectif</h3>
              </div>
              <p className="font-body text-base text-slate-300 leading-relaxed">{program.objective}</p>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-50">Public concerne</h3>
              </div>
              <p className="font-body text-base text-slate-300 leading-relaxed">{program.target_audience}</p>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="font-heading text-xl font-medium text-slate-50">Actions menees</h3>
            </div>
            <ul className="space-y-3">
              {(program.actions || []).map((action, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />
                  <span className="font-body text-base text-slate-300">{action}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
